import { supabaseAdmin } from '../supabase/supabase';

const requireSingleRow = (data, message) => {
  if (!Array.isArray(data) || data.length !== 1) {
    throw new Error(message);
  }

  return data[0];
};

const localDateBoundary = (date, nextDay = false) => {
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day + (nextDay ? 1 : 0)).toISOString();
};

export const adminAuth = {
  getSession: () =>
    supabaseAdmin.auth.getSession(),

  onAuthStateChange: (callback) =>
    supabaseAdmin.auth.onAuthStateChange(callback),

  signIn: (email, password) =>
    supabaseAdmin.auth.signInWithPassword({
      email,
      password,
    }),

  signOut: () =>
    supabaseAdmin.auth.signOut({
      scope: 'global',
    }),

  getAssuranceLevel: () =>
    supabaseAdmin.auth.mfa.getAuthenticatorAssuranceLevel(),

  listFactors: () =>
    supabaseAdmin.auth.mfa.listFactors(),

  enrollTotp: () =>
    supabaseAdmin.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'MyPiggy Administration',
    }),

  unenrollFactor: (factorId) =>
    supabaseAdmin.auth.mfa.unenroll({
      factorId,
    }),

  challengeFactor: (factorId) =>
    supabaseAdmin.auth.mfa.challenge({
      factorId,
    }),

  verifyFactor: ({
    factorId,
    challengeId,
    code,
  }) =>
    supabaseAdmin.auth.mfa.verify({
      factorId,
      challengeId,
      code,
    }),

  refreshSession: () =>
    supabaseAdmin.auth.refreshSession(),
};

export const adminDb = {
  getContext: async () => {
    const { data, error } =
      await supabaseAdmin.rpc(
        'get_my_admin_context'
      );

    if (error) throw error;

    return requireSingleRow(
      data,
      'Unable to load the administrator context.'
    );
  },

  getDashboardSummary: async () => {
    const { data, error } =
      await supabaseAdmin.rpc(
        'admin_dashboard_v2'
      );

    if (error) throw error;

    return data;
  },

  getStripeReconciliationHealth: async () => {
    const { data, error } = await supabaseAdmin.rpc('admin_stripe_reconciliation_health');
    if (error) throw error;
    return data;
  },

  listStripeReconciliationIssues: async () => {
    const { data, error } = await supabaseAdmin.rpc('admin_stripe_reconciliation_issues');
    if (error) throw error;
    return data || [];
  },

  listFamilies: async (search = null, familyStatus = null, entitlementStatus = null, offset = 0) => {
    const normalizedSearch =
      String(search || '').trim() || null;

    const { data, error } =
      await supabaseAdmin.rpc(
        'admin_search_families_v2',
        {
          p_search: normalizedSearch,
          p_family_status: familyStatus,
          p_entitlement_status: entitlementStatus,
          p_offset: offset,
        }
      );

    if (error) throw error;

    return data || { rows: [], total: 0, offset, limit: 25 };
  },

  getFamilyOverview: async (familyId) => {
    const { data, error } =
      await supabaseAdmin.rpc(
        'admin_family_overview_v2',
        {
          p_family_id: familyId,
        }
      );

    if (error) throw error;

    if (!data || typeof data !== 'object') {
      throw new Error(
        'Unable to load the family overview.'
      );
    }

    return data;
  },
  listBillingHistory: async (familyId) => {
    const { data, error } = await supabaseAdmin.rpc('admin_get_billing_history', {
      p_family_id: familyId,
      p_limit: 100,
    });
    if (error) throw error;
    return data || [];
  },
  listNotes: async (familyId) => {
    const { data, error } = await supabaseAdmin.rpc('admin_list_support_notes', { p_family_id: familyId });
    if (error) throw error;
    return data || [];
  },
  listAudit: async (familyId, filters = {}) => {
    const { data, error } = await supabaseAdmin.rpc('admin_list_audit', {
      p_family_id: familyId, p_action: filters.action || null,
      p_administrator_id: filters.administratorId || null,
      p_from: localDateBoundary(filters.from),
      p_to: localDateBoundary(filters.to, true),
      p_offset: filters.offset || 0,
    });
    if (error) throw error;
    return data || [];
  },
  searchAudit: async (filters = {}, offset = 0) => {
    const { data, error } = await supabaseAdmin.rpc('admin_search_audit', {
      p_family_search: filters.family?.trim() || null,
      p_action: filters.actions?.join(',') || null,
      p_administrator_id: filters.administratorId?.trim() || null,
      p_from: localDateBoundary(filters.from),
      p_to: localDateBoundary(filters.to, true),
      p_offset: offset,
    });
    if (error) throw error;
    return data || { rows: [], has_more: false, offset, limit: 25 };
  },
  addNote: async (familyId, note, category, reason, requestId, correctsNoteId = null) => {
    const { data, error } = await supabaseAdmin.rpc('admin_add_support_note', {
      p_family_id: familyId, p_note: note, p_category: category,
      p_reason: reason, p_request_id: requestId,
      p_corrects_note_id: correctsNoteId,
    });
    if (error) throw error;
    return data;
  },
  changeChild: async (familyId, childId, activate, reason, requestId) => {
    const { data, error } = await supabaseAdmin.rpc('admin_change_child_status', {
      p_family_id: familyId, p_child_id: childId, p_activate: activate,
      p_reason: reason, p_request_id: requestId,
    });
    if (error) throw error;
    return data;
  },
  setFamilyReadOnly: async (familyId, readOnly, reason, requestId) => {
    const { data, error } = await supabaseAdmin.rpc('admin_set_family_read_only', {
      p_family_id: familyId, p_read_only: readOnly,
      p_reason: reason, p_request_id: requestId,
    });
    if (error) throw error;
    return data;
  },
  scheduleExpeditedDeletion: async (familyId, reason, requestId) => {
    const { data, error } = await supabaseAdmin.rpc('admin_schedule_expedited_family_deletion', {
      p_family_id: familyId, p_reason: reason, p_request_id: requestId,
    });
    if (error) throw error;
    return data;
  },

  resendInvitation: async (familyId, invitationId, reason) => {
    const { data, error } = await supabaseAdmin.functions.invoke(
      'admin_resend_guardian_invitation',
      { body: { familyId, invitationId, reason } }
    );
    if (error || !data?.success) {
      throw new Error(data?.error || 'Invitation could not be sent.');
    }
    return data;
  },

  recheckStripe: async (familyId, reason) => {
    const { data, error } = await supabaseAdmin.functions.invoke(
      'admin_recheck_stripe', { body: { familyId, reason } }
    );
    if (error || !data?.success) throw new Error(data?.error || 'Stripe could not be checked.');
    return data;
  },

};
